import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Upload, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductForm = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!productId;

  const [formData, setFormData] = useState({
    product_name: '',
    design_number: '',
    primary_image: '',
    additional_images: [],
    description: '',
    variations: [{ size: 'S', color: 'White', fabric: 'Cotton', stock: null }],
    pricing: { bulk: 0, setwise: 0, reselling: 0 }
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/by-id/${productId}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to fetch product');
      navigate('/products');
    }
  };

  const handleImageUpload = async (e, isPrimary = true) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/products/upload-image`, formDataUpload);
      if (isPrimary) {
        setFormData({ ...formData, primary_image: response.data.image_url });
      } else {
        setFormData({
          ...formData,
          additional_images: [...formData.additional_images, response.data.image_url]
        });
      }
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const formDataUpload = new FormData();
    files.forEach(file => {
      formDataUpload.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/products/upload-images-bulk`, formDataUpload);
      setFormData({
        ...formData,
        additional_images: [...formData.additional_images, ...response.data.image_urls]
      });
      toast.success(`${files.length} images uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeAdditionalImage = (index) => {
    const newImages = formData.additional_images.filter((_, i) => i !== index);
    setFormData({ ...formData, additional_images: newImages });
  };

  const addVariation = () => {
    setFormData({
      ...formData,
      variations: [...formData.variations, { size: 'M', color: 'White', fabric: 'Cotton', stock: null }]
    });
  };

  const updateVariation = (index, field, value) => {
    const newVariations = [...formData.variations];
    newVariations[index][field] = value;
    setFormData({ ...formData, variations: newVariations });
  };

  const removeVariation = (index) => {
    const newVariations = formData.variations.filter((_, i) => i !== index);
    setFormData({ ...formData, variations: newVariations });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/products/by-id/${productId}`, formData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success('Product created successfully');
      }
      navigate('/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="product-form-title">
            {isEdit ? 'Edit Product' : 'Create Product'}
          </h1>
          <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {isEdit ? 'Update product details' : 'Add a new product to your catalog'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6 space-y-5 sm:space-y-6" data-testid="product-form">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Product Name</label>
              <input
                type="text"
                required
                data-testid="product-name-input"
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Design Number</label>
              <input
                type="text"
                required
                disabled={isEdit}
                data-testid="design-number-input"
                value={formData.design_number}
                onChange={(e) => setFormData({ ...formData, design_number: e.target.value })}
                className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 text-base"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              rows={3}
              data-testid="description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
            />
          </div>

          {/* Primary Image */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Primary Image</label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              {formData.primary_image && (
                <img src={formData.primary_image} alt="Primary" className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md border" />
              )}
              <label className="cursor-pointer inline-flex items-center px-4 py-2.5 sm:py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start" data-testid="upload-primary-image">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, true)} className="hidden" />
              </label>
            </div>
          </div>

          {/* Additional Images */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Additional Images</label>
            <div className="space-y-3">
              {formData.additional_images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
                  {formData.additional_images.map((image, index) => (
                    <div key={index} className="relative">
                      <img src={image} alt={`Additional ${index + 1}`} className="w-full aspect-square object-cover rounded-md border" />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-rose-500 text-white rounded-full p-0.5 sm:p-1 hover:bg-rose-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="cursor-pointer inline-flex items-center justify-center px-4 py-2.5 sm:py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors text-sm sm:text-base" data-testid="upload-additional-image">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Single'}
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, false)} className="hidden" disabled={uploading} />
                </label>
                <label className="cursor-pointer inline-flex items-center justify-center px-4 py-2.5 sm:py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm sm:text-base" data-testid="upload-bulk-images">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                  <input type="file" accept="image/*" multiple onChange={handleBulkImageUpload} className="hidden" disabled={uploading} />
                </label>
              </div>
              <p className="text-xs text-slate-500">You can select multiple images for bulk upload</p>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Pricing</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Bulk Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  data-testid="bulk-price-input"
                  value={formData.pricing.bulk}
                  onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, bulk: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Set-wise Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  data-testid="setwise-price-input"
                  value={formData.pricing.setwise}
                  onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, setwise: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Reselling Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  data-testid="reselling-price-input"
                  value={formData.pricing.reselling}
                  onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, reselling: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2.5 sm:py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                />
              </div>
            </div>
          </div>

          {/* Variations */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-slate-700">Variations</label>
              <button
                type="button"
                onClick={addVariation}
                data-testid="add-variation-button"
                className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
            <div className="space-y-3">
              {formData.variations.map((variation, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-3 bg-slate-50 rounded-lg sm:items-center" data-testid={`variation-${index}`}>
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Size"
                      value={variation.size}
                      onChange={(e) => updateVariation(index, 'size', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Color"
                      value={variation.color}
                      onChange={(e) => updateVariation(index, 'color', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Fabric"
                      value={variation.fabric}
                      onChange={(e) => updateVariation(index, 'fabric', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeVariation(index)}
                    className="p-2 text-rose-600 hover:bg-rose-50 rounded-md transition-colors self-end sm:self-auto"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-product-button"
              className="flex-1 px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] disabled:opacity-50 text-base"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/products')}
              data-testid="cancel-button"
              className="px-6 py-3 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors text-base"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};
